import React, { useEffect, useRef, useState } from 'react'
import ConnectionModal from './ConnectionModal'

// The URL we probe to confirm a *real* outage (the backend the app already talks
// to). Any HTTP response — even a 404 — means we're online; only a true network
// failure rejects.
const PROBE_URL = `${import.meta.env.VITE_BACKEND_API_URL ?? ''}/`

/**
 * navigator.onLine is unreliable: it reports "offline" on brief wifi handoffs,
 * VPN toggles, sleep/wake and OS connectivity flaps even when the connection is
 * perfectly fine — which is why this modal kept popping while the dashboard was
 * loading data. So we no longer trust it blindly: when it claims we're offline
 * we fire a real network probe, and only block the app if THAT fails. False
 * alarms never show the modal; a genuine outage still does.
 */
const NetworkDetector: React.FC = () => {
  const [isOffline, setIsOffline] = useState<boolean>(false)
  const checking = useRef(false)

  // A real request: a network failure rejects; any reachable server resolves
  // (no-cors → opaque response, which is all we need to prove connectivity).
  const reallyOffline = async (): Promise<boolean> => {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), 5000)
    try {
      await fetch(PROBE_URL, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal: ctrl.signal,
      })
      return false
    } catch {
      return true
    } finally {
      clearTimeout(timer)
    }
  }

  const checkConnection = async () => {
    // Browser is confident we're online → never block.
    if (navigator.onLine) {
      setIsOffline(false)
      return
    }
    // Browser claims offline — confirm with a real probe before blocking the app.
    if (checking.current) return
    checking.current = true
    try {
      setIsOffline(await reallyOffline())
    } finally {
      checking.current = false
    }
  }

  const handleRetry = () => {
    void checkConnection()
  }

  useEffect(() => {
    void checkConnection()

    window.addEventListener('online', checkConnection)
    window.addEventListener('offline', checkConnection)

    const intervalId = setInterval(() => {
      void checkConnection()
    }, 5000)

    return () => {
      window.removeEventListener('online', checkConnection)
      window.removeEventListener('offline', checkConnection)
      clearInterval(intervalId)
    }
  }, [])

  return <ConnectionModal isVisible={isOffline} onRetry={handleRetry} />
}

export default NetworkDetector
